import type {
  Admin1Dataset,
  CitiesDataset,
  City,
  CountriesDataset,
  Country,
  CountryPolygonsTopology,
} from './schemas';
import { applyCountryOverride } from './overrides';

/**
 * Default location of data files. Assumes they're served from the same
 * origin as the page at `/data/*.json` — true for our Cloudflare Pages
 * setup where `packages/web/public/data/` is the Vite public dir.
 *
 * Override via `baseUrl` option when hosting data on a CDN subdomain
 * (e.g. `cdn.atlasguess.com/data`) or during local dev against a different
 * environment.
 */
const DEFAULT_DATA_BASE_URL = '/data';

export interface LoadDataOptions {
  /** Base URL for data files. Default: '/data' (same-origin, Vite public dir). */
  baseUrl?: string;
  /** Abort the fetch via an AbortController signal. */
  signal?: AbortSignal;
}

// Simple per-URL cache — data is immutable per session; no need to re-fetch.
const cache = new Map<string, Promise<unknown>>();

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const existing = cache.get(url);
  if (existing) return existing as Promise<T>;

  const promise = (async (): Promise<T> => {
    const res = await fetch(url, { signal });
    if (!res.ok) {
      throw new Error(`Failed to fetch ${url}: ${res.status} ${res.statusText}`);
    }
    return (await res.json()) as T;
  })();

  cache.set(url, promise);
  return promise.catch((err) => {
    cache.delete(url); // allow retry on next call after failure
    throw err;
  });
}

/**
 * Load the normalized country list. Returns the full dataset including the
 * `_meta` block. For just the country array use `getCountryList`.
 *
 * Applies the per-country overrides (`overrides.ts`) on first load —
 * subsequent calls hit the cache and see the corrected data already in
 * place. The mutation is idempotent.
 */
export async function loadCountries(options: LoadDataOptions = {}): Promise<CountriesDataset> {
  const base = options.baseUrl ?? DEFAULT_DATA_BASE_URL;
  const dataset = await fetchJson<CountriesDataset>(`${base}/countries.json`, options.signal);
  for (const country of dataset.countries) applyCountryOverride(country);
  return dataset;
}

/** Shortcut — just the country array, no _meta wrapper. */
export async function getCountryList(options: LoadDataOptions = {}): Promise<Country[]> {
  const dataset = await loadCountries(options);
  return dataset.countries;
}

/**
 * Load the TopoJSON topology containing all country polygons. Step 6
 * (borders rendering) consumes this — not needed by any game-mode logic
 * that only cares about metadata + centroids.
 */
export function loadCountryPolygons(
  options: LoadDataOptions = {},
): Promise<CountryPolygonsTopology> {
  const base = options.baseUrl ?? DEFAULT_DATA_BASE_URL;
  return fetchJson<CountryPolygonsTopology>(`${base}/country-polygons.json`, options.signal);
}

/**
 * Load admin1 (state/province) polygons for a specific country. Phase 1
 * ships with US only. Requesting an unsupported country hits a 404 —
 * callers should catch and degrade gracefully.
 *
 * Files are per-country to keep bandwidth minimal: a session playing the
 * "name all US states" game downloads ~350 KB, not the full 8-12 MB of
 * global admin1 data.
 */
export function loadAdmin1(
  iso: string,
  options: LoadDataOptions = {},
): Promise<Admin1Dataset> {
  const base = options.baseUrl ?? DEFAULT_DATA_BASE_URL;
  const normalizedIso = iso.toUpperCase();
  return fetchJson<Admin1Dataset>(
    `${base}/admin1-${normalizedIso}.json`,
    options.signal,
  );
}

/** Options for `loadCities` / `getCityList`. `tierMax` is required so game
 * modes have to think about how big a slice they actually need. */
export interface LoadCitiesOptions extends LoadDataOptions {
  /**
   * Tier ceiling — fetches the smallest cumulative file that includes every
   * city at this tier or lower. Tiers are cumulative (`tierMax: 2` returns
   * tier-1 + tier-2 cities). Pick the smallest tier that covers your
   * difficulty range; loading more is wasted bandwidth.
   *
   *   1 — primary country capitals only (~50 KB / ~12 KB gzipped)
   *   2 — capitals + top non-capital cities (~120 KB / ~30 KB gzipped)
   *   3 — above + next bracket (~470 KB / ~120 KB gzipped)
   *   4 — everything (~1.7 MB / ~400 KB gzipped — expert mode only)
   */
  tierMax: 1 | 2 | 3 | 4;
}

/**
 * Load the normalized cities dataset for a given tier ceiling. Returns the
 * full dataset including the `_meta` block. For just the city array use
 * `getCityList`.
 *
 * Lazy by design — the engine never auto-fetches this. Game modes call
 * this at setup with the smallest `tierMax` that covers their needs.
 *
 * Tiers are cumulative: `tierMax: 2` fetches `cities-tier-2.json` which
 * already contains every tier-1 city. Per-URL caching means a game mode
 * that calls `loadCities({ tierMax: 2 })` then `loadCities({ tierMax: 3 })`
 * will fetch each file once.
 */
export function loadCities(options: LoadCitiesOptions): Promise<CitiesDataset> {
  const base = options.baseUrl ?? DEFAULT_DATA_BASE_URL;
  return fetchJson<CitiesDataset>(`${base}/cities-tier-${options.tierMax}.json`, options.signal);
}

/** Shortcut — just the city array, no _meta wrapper. */
export async function getCityList(options: LoadCitiesOptions): Promise<City[]> {
  const dataset = await loadCities(options);
  return dataset.cities;
}

/**
 * Filter criteria for `filterCities`. All criteria are AND-combined; pass
 * `undefined` (or omit) to skip that criterion. Composes cleanly so a game
 * mode can express "all African capitals" or "tier ≤ 3 cities outside the
 * G20" without enumerating IDs.
 */
export interface CityFilterCriteria {
  /** Keep only cities with `importance_tier <= tierMax`. */
  tierMax?: 1 | 2 | 3 | 4;
  /** Keep only cities whose `is_capital === true` (primary country capitals). */
  capitalsOnly?: boolean;
  /**
   * Keep only cities whose country's continent matches one of these. Requires
   * passing `countryByIso` so the filter can join — otherwise this criterion
   * is silently skipped.
   */
  continents?: string[];
  /** Keep only cities whose `country_iso` is in this list. */
  countryIsos?: string[];
  /**
   * Force-include these IDs even if they fail other criteria. Order is preserved
   * (forced cities come first). Useful for curated lists / handpicked additions.
   */
  includeIds?: string[];
  /** Force-exclude these IDs (sensitive / inappropriate / broken data). */
  excludeIds?: string[];
  /** Drop cities whose `hidden` flag is true (default: true). */
  excludeHidden?: boolean;
  /**
   * ISO → Country lookup, required if `continents` is used. Caller passes
   * the result of `getCountryList` indexed by iso_a2.
   */
  countryByIso?: Map<string, Country>;
}

/**
 * Apply a `CityFilterCriteria` to a city list. Pure / synchronous — no
 * fetching. Game modes typically call `getCityList()` once and then
 * `filterCities` per round / per category.
 */
export function filterCities(cities: City[], criteria: CityFilterCriteria = {}): City[] {
  const {
    tierMax,
    capitalsOnly,
    continents,
    countryIsos,
    includeIds,
    excludeIds,
    excludeHidden = true,
    countryByIso,
  } = criteria;

  const includeSet = includeIds ? new Set(includeIds) : null;
  const excludeSet = excludeIds ? new Set(excludeIds) : null;
  const isoSet = countryIsos ? new Set(countryIsos) : null;
  const continentSet = continents ? new Set(continents) : null;

  // Bucket forced-in IDs first so they can override the standard filters
  // — matches the design intent ("curated list always wins").
  const forced: City[] = [];
  const filtered: City[] = [];

  for (const city of cities) {
    if (excludeSet?.has(city.id)) continue;
    if (includeSet?.has(city.id)) {
      forced.push(city);
      continue;
    }
    if (excludeHidden && city.hidden) continue;
    if (tierMax !== undefined && city.importance_tier > tierMax) continue;
    if (capitalsOnly && !city.is_capital) continue;
    if (isoSet && !isoSet.has(city.country_iso)) continue;
    if (continentSet) {
      if (!countryByIso) continue; // can't evaluate continent without the lookup
      const country = countryByIso.get(city.country_iso);
      if (!country || !continentSet.has(country.continent)) continue;
    }
    filtered.push(city);
  }

  return [...forced, ...filtered];
}

/** Clear the in-memory cache — useful in tests or hot-reload scenarios. */
export function clearDataCache(): void {
  cache.clear();
}
